import { SelectOption } from '@leasequery/lq.react-component-library/dist/models';
import { Autocomplete } from '@leasequery/lq.react-component-library';
import React, { useCallback, useEffect, useState } from 'react';
import { fetchAdminClients } from '../../api';
import { QuickActionContainer } from '../../components/quick-actions/styled';

import { useAuth } from '../../context';
import { ClientListItem, Company } from '../../models';
import { useRouter } from 'next/router';

import {
  CenteredContainer,
  QuickAction,
  RecentsQuickActions,
  DisabledContainer,
} from '../../components';
import * as S from './styled';
import { putClientSetting } from '../../api/users/saveSelectedClientId';
import { useFeatureFlags } from '../../context/featureFlags';
import { FeatureFlagKeys } from '../../constants/featureFlags';

function LandingPage(): JSX.Element {
  const [autoCompleteOptions, setAutoCompleteOptions] = useState<
    Array<SelectOption<string>>
  >([]);

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [showClientSelector, setShowClientSelector] = useState<boolean>(false);

  const { user, client, setClient, clientSetting } = useAuth();
  const router = useRouter();
  const { clientId } = router.query;

  const [selectedClient, setSelectedClient] = useState<SelectOption<string>>({
    key: client?.clientId,
    value: client?.companyName || '',
    label: client?.companyName || '',
  });

  const { isFeatureEnabled } = useFeatureFlags();
  const isClientSelectorRemovalEnabled = isFeatureEnabled(FeatureFlagKeys.ClientSelectorRemovalEnabled);

  const getClients = async (): Promise<Array<SelectOption<string>>> => {
    const adminClients = await fetchAdminClients();
    const adminClientsAvailable = adminClients.map((client: ClientListItem) => {
      return {
        key: client.id,
        value: client.companyName,
        label: client.companyName,
      };
    });
    setAutoCompleteOptions(adminClientsAvailable);
    return adminClientsAvailable;
  };

  const clientSelected = useCallback(
    (client: SelectOption<string>): void => {
      if (client === null || client === undefined) {
        return;
      }
      const clientFromSelected: Company = {
        clientId: client.key,
        companyName: client.label,
      };
      setClient(clientFromSelected);
      setSelectedClient(client);
      putClientSetting(client.key);
    },
    [setClient],
  );

  useEffect(() => {
    if (user.canAccessMultipleClients) {
      setShowClientSelector(true);
      getClients().then((clients) => {
        if (clientId) {
          const client = clients.find((x) => x.key === Number(clientId));
          if (client) clientSelected(client);
        }
      });
    } else { 
      if(user.isMultiClientUser) {
        getClients().then((clients)=> {
          clientSelected(clients[0]);
        })
        return;
      }
      clientSelected({key: user.clientId, label: user.companyName, value: user.companyName})
    }
  }, [setClient, user, clientId, clientSelected]);

  useEffect(() => {
    if (
      showClientSelector &&
      (selectedClient?.label === undefined || selectedClient?.label === '')
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [selectedClient, user, showClientSelector]);

  return (
    <QuickActionContainer>
      {showClientSelector && !isClientSelectorRemovalEnabled ? (
        <S.ClientSelector>
          <Autocomplete
            name="test"
            options={autoCompleteOptions}
            placeholder={'Begin typing to search for client'}
            onChange={clientSelected}
            value={selectedClient}
            required={false}
            arrow={true}
            key={autoCompleteOptions.length}
          />
        </S.ClientSelector>
      ): null}
      <DisabledContainer className={isDisabled && 'disabled'}>
        <CenteredContainer>
          <QuickAction
            title="Find Reports"
            description="Search reports or explore standard reports by jurisdiction or type."
            image="find"
            href="/find"
            disabled={isDisabled}
          />
          <QuickAction
            title="Create Reports"
            description="Build the exact report you need. Select specific fields and customize field rules."
            image="create"
            href="/create"
            disabled={isDisabled}
          />
          <QuickAction
            title="Report Results"
            description="See previously run reports, archived reports, and saved report criteria."
            image="results"
            href="/results"
            disabled={isDisabled}
          />
          {clientSetting?.useBackgroundTask && (
            <QuickAction
              title="Export Results"
              description="See results of exports to FTPs or ERPs and get CSV downloads made from the Journal Entries page."
              image="export"
              href="/export"
              disabled={isDisabled}
            />
          )}
        </CenteredContainer>
        <RecentsQuickActions
          disabled={isDisabled}
          client={selectedClient.key}
        />
      </DisabledContainer>
    </QuickActionContainer>
  );
}

export default LandingPage;
